/**
 * Pure functions for signatory validity resolution.
 * No I/O — all logic is deterministic based on inputs.
 *
 * AF-2757: Seleção de signatários por empresa.
 */

import type { Signatory } from '../../../services/personApi';
import { PERSON_PRODUCTS } from '../../../constants/personProducts';

export interface SignatoryValidity {
    valid: boolean;
    reasons: string[];
}

/**
 * Minimum number of signatories required per representationFormat value.
 * Legacy values (JOINT_TWO_DIRECTORS, JOINT, ISOLATED_OR_JOINT, ISOLATED_AND_OR_JOINT, DEPENDS_OBLIGATION)
 * are treated permissively to avoid blocking existing operations.
 */
export const REPRESENTATION_MINIMUM: Record<string, number> = {
    ISOLATED: 1,
    TWO_PERSONS: 2,
    THREE_PERSONS: 3,
    JOINT_TWO_DIRECTORS: 2,
    JOINT: 2,
    ISOLATED_OR_JOINT: 1,
    ISOLATED_AND_OR_JOINT: 1,
    DEPENDS_OBLIGATION: 1,
};

/**
 * Resolves the validity of a single signatory for a given contract.
 *
 * Rules:
 * 1. Must be active (isActive === true)
 * 2. Must have at least one signAs entry — without a registered signature profile there is
 *    no evidence the person may sign on behalf of the company
 * 3. If signAs is "Procurador": proxyExpirationDate must not be expired (if set),
 *    and proxyAllowedValue must be empty OR >= contractValue
 * 4. Must have at least one allowedOperations[].operation matching the allowedProducts list
 *    (if allowedProducts is non-empty; empty = no filter = retrocompatible)
 *
 * Email absence does NOT invalidate display — it blocks sending if selected.
 */
export function resolveSignatoryValidity(
    signatory: Signatory,
    contractValue: number | null | undefined,
    allowedProducts: string[],
): SignatoryValidity {
    const reasons: string[] = [];

    // Rule 1: Active
    if (!signatory.isActive) {
        reasons.push('Signatário inativo');
    }

    // Rule 2: Must have a signature profile (signAs).
    // Without it there is no way to assert this person can sign on behalf of the company,
    // so signing is not allowed even if everything else checks out.
    const signAsEntries = signatory.signAsEntries || [];
    if (signAsEntries.length === 0) {
        reasons.push('Sem perfil de assinatura cadastrado (firmas e poderes)');
    }

    // Rule 3: Proxy validation (for Procurador)
    const isProcurador = signAsEntries.some(
        e => (e.signAs || '').toLowerCase().includes('procurador')
    );
    if (isProcurador) {
        // Check proxy expiration
        if (signatory.proxyExpirationDate) {
            // Parse date-only strings (YYYY-MM-DD) as local date to avoid timezone shift.
            // new Date('2025-07-29') interprets as UTC midnight which shifts day in BRT.
            const raw = signatory.proxyExpirationDate;
            const parts = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
            const expirationDate = parts
                ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
                : new Date(raw);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (expirationDate < today) {
                reasons.push(`Procuração vencida em ${expirationDate.toLocaleDateString('pt-BR')}`);
            }
        }
        // Check proxy allowed value
        if (
            signatory.proxyAllowedValue != null &&
            signatory.proxyAllowedValue > 0 &&
            contractValue != null &&
            contractValue > 0 &&
            signatory.proxyAllowedValue < contractValue
        ) {
            reasons.push(
                `Valor da procuração (${formatCurrency(signatory.proxyAllowedValue)}) inferior ao valor do contrato (${formatCurrency(contractValue)})`
            );
        }
    }

    // Rule 4: Product compatibility
    if (allowedProducts.length > 0) {
        const ops = normalizeOperations(signatory.allowedOperations);
        const hasMatch = ops.some(op => allowedProducts.includes(op));
        if (!hasMatch) {
            reasons.push(`Signatário sem vínculo com os produtos: ${allowedProducts.join(', ')}`);
        }
    }

    return {
        valid: reasons.length === 0,
        reasons,
    };
}

/**
 * Computes the required minimum number of signatories for a company,
 * based on the representationFormat of the selected signatories.
 *
 * Rule: exigido = highest REPRESENTATION_MINIMUM among the selected signatories'
 * operations that are relevant to this contract (filtered by allowedProducts).
 * If allowedProducts is empty, all operations are considered (retrocompatible).
 * If none are selected, returns 0 (minCount from ContractType governs).
 */
export function requiredSignatureCount(
    selectedSignatories: Signatory[],
    allowedProducts: string[] = [],
): number {
    if (selectedSignatories.length === 0) return 0;

    let maxRequired = 0;
    for (const sig of selectedSignatories) {
        const min = getSignatoryMinRequired(sig, allowedProducts);
        if (min > maxRequired) maxRequired = min;
    }
    return maxRequired;
}

/**
 * Returns the minimum number of signatures this signatory demands,
 * based on their most restrictive representationFormat among relevant products.
 */
function getSignatoryMinRequired(sig: Signatory, allowedProducts: string[]): number {
    const ops = sig.allowedOperations || [];
    let max = 0;
    for (const op of ops) {
        const opName = typeof op === 'string' ? op : op.operation;
        if (allowedProducts.length > 0 && !allowedProducts.includes(opName)) continue;
        if (!(PERSON_PRODUCTS as readonly string[]).includes(opName)) continue;
        const format = typeof op === 'string' ? 'ISOLATED' : (op.representationFormat || 'ISOLATED');
        const min = REPRESENTATION_MINIMUM[format] ?? 1;
        if (min > max) max = min;
    }
    return max;
}

/**
 * Checks if the representation requirement is satisfied for a company's selected signatories.
 * Returns null if satisfied, or a descriptive message if not.
 */
export function checkRepresentationRequirement(
    selectedSignatories: Signatory[],
    allowedProducts: string[] = [],
): string | null {
    if (selectedSignatories.length === 0) return null; // minCount governs, not representation
    const required = requiredSignatureCount(selectedSignatories, allowedProducts);
    if (selectedSignatories.length >= required) return null;
    return `Representação exige ${required} assinatura(s) — ${selectedSignatories.length} selecionada(s)`;
}

// --- Helpers ---

function normalizeOperations(ops: Signatory['allowedOperations']): string[] {
    if (!ops) return [];
    return ops.map(op => (typeof op === 'string' ? op : op.operation));
}

function formatCurrency(value: number): string {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}


// --- Role compatibility based on signAs profile ---

/**
 * Maps a signAs value (from the Person registry) to the contract roles that person
 * is allowed to exercise. Case-insensitive matching is used.
 *
 * "Representante legal" and "Procurador" can sign any role compatible with the company's
 * participantSource (they act on behalf of the company in any capacity).
 *
 * "Fiel depositário(a)" is an accessory function that doesn't restrict the contract role.
 */
const SIGN_AS_TO_ROLES: Record<string, string[] | 'ALL'> = {
    // Authority roles — can sign in any capacity on behalf of the company
    'representante legal': 'ALL',
    'responsável legal': 'ALL',
    'procurador': 'ALL',
    'fiel depositário(a)': 'ALL',
    'diretor(a)': 'ALL',
    'presidente': 'ALL',
    'sócio(a)': 'ALL',
    'contratada': 'ALL',
    'contratante': 'ALL',
    'outorgante': 'ALL',
    'outorgado(a)': 'ALL',
    // Creditor-side roles
    'credor(a)': ['CREDORA', 'CESSIONARIA', 'CREDORA_FIDUCIARIA'],
    'cessionário': ['CESSIONARIA'],
    'endossatário': ['CESSIONARIA', 'CEDENTE'],
    // Debtor-side roles
    'cedente': ['CEDENTE', 'EMITENTE'],
    'emitente': ['EMITENTE', 'CEDENTE'],
    'devedor(es)': ['DEVEDOR_FIDUCIARIO', 'EMITENTE'],
    'devedor solidário': ['AVALISTA', 'DEVEDOR_FIDUCIARIO', 'EMITENTE'],
    'endossante': ['CEDENTE', 'CESSIONARIA'],
    // Guarantor roles
    'interveniente garantidor': ['AVALISTA', 'INTERVENIENTE_ANUENTE'],
    'responsável solidário': ['AVALISTA', 'INTERVENIENTE_ANUENTE'],
    'fiador': ['AVALISTA'],
    'cônjuge do fiador': ['AVALISTA'],
    'afiançado': ['AVALISTA'],
    'avalista': ['AVALISTA'],
    // Consenting/intervening roles
    'interveniente anuente': ['INTERVENIENTE_ANUENTE'],
    'interveniente': ['INTERVENIENTE_ANUENTE'],
    'anuente': ['INTERVENIENTE_ANUENTE'],
    // Witness/validator roles
    'testemunha': ['TESTEMUNHA', 'VALIDADOR'],
    'validador': ['VALIDADOR', 'TESTEMUNHA'],
};

/**
 * Returns the contract roles a signatory is allowed to exercise, based on their
 * signAsEntries. If any entry grants 'ALL', the full availableRoles list is returned.
 */
export function getAllowedRolesForSignatory(
    signatory: Signatory,
    availableRoles: { value: string; label: string }[],
): { value: string; label: string }[] {
    const entries = signatory.signAsEntries || [];
    if (entries.length === 0) return []; // no profile = no roles

    // Collect all allowed role values from the signAs entries
    const allowedRoleValues = new Set<string>();
    let hasAll = false;

    for (const entry of entries) {
        const signAsLower = (entry.signAs || '').toLowerCase().trim();
        if (!signAsLower) continue; // Skip empty/undefined signAs to avoid matching everything

        // Match strategy: exact first, then substring, then fallback ALL.
        // This avoids "interveniente" matching "interveniente garantidor" by accident.
        const exactMatch = Object.entries(SIGN_AS_TO_ROLES).find(
            ([key]) => signAsLower === key
        );
        const substringMatch = !exactMatch
            ? Object.entries(SIGN_AS_TO_ROLES).find(
                ([key]) => signAsLower.includes(key) || key.includes(signAsLower)
            )
            : undefined;
        const mapping = exactMatch || substringMatch;

        if (mapping) {
            const [, roles] = mapping;
            if (roles === 'ALL') {
                hasAll = true;
                break;
            }
            roles.forEach(r => allowedRoleValues.add(r));
        } else {
            // Fallback: signAs not in the map → grant ALL roles.
            // This ensures new profiles created in the Person registry work immediately
            // without requiring a code change to map them.
            hasAll = true;
            break;
        }
    }

    if (hasAll) return availableRoles;
    return availableRoles.filter(r => allowedRoleValues.has(r.value));
}
